/**
 * etapa.tsx — A PÁGINA DE UMA ETAPA do acompanhamento.
 *
 * Pedido dele em 04/09/2026, olhando o `forum-details` do tema: *"cada uma
 * dessas etapas clicável. Aí na hora que você entra na etapa você tem um campo
 * para logar. **Não vai ser um modal porque o modal não vai ficar bom** — não
 * vai ter espaço para ter conversa, para você ter um chat. Cria a página e cria
 * o chat ali dentro, usa de repente a coluna da direita para contextualizar."*
 *
 * ─── A ESTRUTURA É A DO `ForumDetails.tsx` ──────────────────────────────────
 * `col-12 col-md-8 col-lg-9` com o assunto e a conversa, e `col-12 col-md-4
 * col-lg-3` com o contexto ao lado — no fórum, "Featured Questions"; aqui, o
 * pedido a que a etapa pertence e as etapas vizinhas, que é justamente o que
 * some quando se sai da lista.
 *
 * ─── SEM ENDPOINT NOVO ──────────────────────────────────────────────────────
 * A página carrega o pedido e a LISTA de etapas (as duas rotas que já existem)
 * e acha a sua pelo id da URL. Isso dá de graça a coluna da direita: as outras
 * etapas já estão na mão, com estado e dono.
 */
import { useCallback, useEffect, useState, useMemo } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Tab, Tabs } from 'react-bootstrap';
import { toast } from 'sonner';
import { api, ApiError } from '@/lib/api';
import { formatarData } from '@/lib/data';
import { useAuth } from '@/auth/auth-context';
import { CustomCampoData } from '@/components/CustomCamposData';
import { CustomMolduraPagina } from '@/components/CustomMolduraPagina';
import { CustomDica } from '@/components/CustomDica';
import { CustomMensagensBadge, CustomMensagensDoItem, CustomMensagensPrevia } from '@/components/CustomMensagens';
import { CustomAtividades } from './CustomAtividades';
import { CustomDocumentos } from './CustomDocumentos';
import type { Autorizacao } from './shared';
import type { Etapa } from './CustomEtapas';
import { numerosDasEtapas, porLinha } from './CustomEtapasLista';
import { CustomEtapaConclusao } from './CustomEtapaConclusao';
import type { UsuarioOpcao } from './shared';
import { etapaAberta } from './shared';

/** O mesmo vocabulário do card — dois nomes para o mesmo estado seria pior. */
/** Os cinco rótulos aprovados em 05/09 — o mesmo vocabulário do card, porque
 *  dois nomes para o mesmo estado seria pior que nenhum. Vencida não entra:
 *  é alerta que atravessa os cinco (ver `estadoDaEtapa` na lista). */
/** O ESTADO — o mesmo do card, e o mesmo que o select oferece. */
function selo(e: Etapa) {
  if (e.estado === 'feita') return { rotulo: 'Concluída', cor: 'casaa-selo-sucesso' };
  if (e.estado === 'dispensada') return { rotulo: 'Dispensada', cor: 'casaa-selo-neutro' };
  if (e.estado === 'em_andamento') return { rotulo: 'Em andamento', cor: 'casaa-selo-destaque' };
  return { rotulo: 'Não iniciada', cor: 'casaa-selo-neutro' };
}

/** A FILA — calculada, e só enquanto não começou. Ver `CustomEtapasLista`. */
function fila(e: Etapa) {
  if (e.estado !== 'aberta') return null;
  return e.na_vez
    ? { rotulo: 'Liberada', cor: 'casaa-selo-info' }
    : { rotulo: 'Bloqueada', cor: 'casaa-selo-atencao' };
}

/** O número da etapa (1 · 2.1 · 2.2), calculado como na lista. */
function numeroDe(etapas: Etapa[], id: number): string {
  const linhas = porLinha(etapas);
  for (let i = 0; i < linhas.length; i += 1) {
    const j = linhas[i].findIndex((e) => e.id === id);
    if (j >= 0) return linhas[i].length > 1 ? `${i + 1}.${j + 1}` : `${i + 1}`;
  }
  return '—';
}

/**
 * Um campo com a sua DICA ao lado.
 *
 * O (i) fica FORA do `form-floating`, não dentro: no Bootstrap 5 o rótulo
 * flutuante tem `pointer-events: none`, então um ícone colado nele nunca
 * receberia o mouse. Aqui ele é irmão do campo, e por isso também chega pelo
 * Tab e pelo toque no celular.
 */
function CampoComDica({ dica, children }: { dica: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="d-flex align-items-start gap-1">
      <div className="flex-grow-1">{children}</div>
      <div className="pt-3"><CustomDica texto={dica} /></div>
    </div>
  );
}

export function AutorizacaoEtapaPage() {
  const { id, etapaId } = useParams();
  const navegar = useNavigate();
  const { pode } = useAuth();
  const podeGerir = pode('autorizacoes.etapas.gerir');

  const [aut, setAut] = useState<Autorizacao | null>(null);
  const [etapas, setEtapas] = useState<Etapa[]>([]);
  /* Os números das etapas ("2.0") para a etiqueta da conversa — a MESMA função
     do card, porque dois números para a mesma etapa seria pior que nenhum. */
  const numerosPorEtapa = useMemo(
    () => Object.fromEntries(numerosDasEtapas(etapas)) as Record<number, string>,
    [etapas],
  );
  const [usuarios, setUsuarios] = useState<UsuarioOpcao[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  /** Concluir acontece AQUI: mandar a pessoa de volta à lista era bengala. */
  const [concluindo, setConcluindo] = useState(false);
  /* ADIAR DEIXOU DE SER UM CLIQUE (05/09): "ela vai adiar uma semana — por
     quê?". O motivo é obrigatório na API, e vira a linha de atividade. */
  const [adiando, setAdiando] = useState(false);
  const [motivoAdiar, setMotivoAdiar] = useState('');

  /* ─── A EDIÇÃO ────────────────────────────────────────────────────────────
     *"Eu entro aqui e faço o quê? Nada. Não posso editar, não posso mudar
     status"* (04/09/2026). A página repetia o CARD, e card é para LER.
     Aqui o mesmo conteúdo abre em FORMULÁRIO — os campos que o `PUT
     /autorizacoes/etapas/{id}` já aceita, mais a situação, que não é campo:
     ela vira POST de conclusão ou de reabertura conforme o lado para onde
     se anda. */
  /* ⛔ A PÁGINA ABRE EM FORMULÁRIO, não em card de leitura. Dele, 05/09:
     *"não dá para continuar assim com esse card — fica meio que indistinguível
     da outra que mostra o card completo. Ela entra e já entra na forma de
     formulário, você vê o que pode editar ou não"*. E é justo: a lista já
     mostra o card: repeti-lo aqui fazia a página parecer a mesma tela sem
     nenhuma ação a mais.

     Quem não pode gerir continua vendo a leitura — o formulário só existe para
     quem mexe. */
  /* A aba fica no estado: sair da página já devolve o acompanhamento, e mais
     um parâmetro na URL só encompridaria o link. */
  const [abaEtapa, setAbaEtapa] = useState<'etapa' | 'atividades' | 'documentos' | 'chat'>('etapa');
  const [editando, setEditando] = useState(false);
  const [eTitulo, setETitulo] = useState('');
  const [eDetalhe, setEDetalhe] = useState('');
  const [eResponsavel, setEResponsavel] = useState('');
  const [ePrazo, setEPrazo] = useState('');
  const [eSituacao, setESituacao] = useState<'aberta' | 'em_andamento' | 'feita' | 'dispensada'>('aberta');
  /* Quem pode pôr a etapa EM ANDAMENTO: o responsável escolhido no formulário
     — ou ninguém escolhido, que é o caso de pegar a etapa para si. Olha o
     campo, e não o que está gravado, porque atribuir-se e começar cabem no
     mesmo salvamento. */
  const [salvandoEdicao, setSalvandoEdicao] = useState(false);

  const carregar = useCallback(async () => {
    try {
      const [pedido, lista] = await Promise.all([
        api.get<Autorizacao>(`/autorizacoes/${id}`),
        api.get<Etapa[]>(`/autorizacoes/${id}/etapas`),
      ]);
      setAut(pedido);
      setEtapas(lista);
      setErro(null);
    } catch (err) {
      setErro(err instanceof ApiError ? err.message : 'Não deu para abrir a etapa.');
    } finally {
      setCarregando(false);
    }
  }, [id]);

  useEffect(() => { carregar(); }, [carregar]);

  /* Assim que a etapa chega, o formulário já vem preenchido — e aberto, para
     quem pode mexer. O `etapaId` na dependência troca os campos ao andar de uma
     etapa para outra sem sair da página. */
  useEffect(() => {
    const e = etapas.find((x) => String(x.id) === etapaId);
    if (!e) return;
    setETitulo(e.titulo);
    setEDetalhe(e.detalhe ?? '');
    setEResponsavel(e.responsavel_id ? String(e.responsavel_id) : '');
    setEPrazo(e.prazo ?? '');
    setESituacao(e.estado);
    setEditando(podeGerir);
  }, [etapas, etapaId, podeGerir]);

  // Só serve ao "e o que vem depois?" do formulário de conclusão.
  useEffect(() => {
    api.get<UsuarioOpcao[]>('/usuarios/opcoes').then(setUsuarios).catch(() => undefined);
  }, []);

  async function salvarEdicao(e: Etapa) {
    if (!eTitulo.trim() || salvandoEdicao) return;
    setSalvandoEdicao(true);
    try {
      await api.put(`/autorizacoes/etapas/${e.id}`, {
        titulo: eTitulo.trim(),
        detalhe: eDetalhe.trim() || null,
        responsavel_id: eResponsavel ? Number(eResponsavel) : null,
        prazo: ePrazo || null,
      });
      /* ⛔ A SITUAÇÃO NÃO É CAMPO DO PUT: cada uma é um ATO, com a sua rota e
         o seu carimbo de quem fez. Mandar tudo para a conclusão era o defeito
         que ele encontrou em 05/09 — escolher "Em andamento" chamava a rota de
         concluir (que só aceita concluída/dispensada) e a mudança sumia sem
         erro nenhum na tela. */
      if (eSituacao !== e.estado) {
        if (eSituacao === 'aberta') {
          await api.post(`/autorizacoes/etapas/${e.id}/reabertura`);
        } else if (eSituacao === 'em_andamento') {
          await api.post(`/autorizacoes/etapas/${e.id}/inicio`);
        } else {
          await api.post(`/autorizacoes/etapas/${e.id}/conclusao`, { estado: eSituacao });
        }
      }
      setEditando(false);
      carregar();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Não deu para salvar.');
    } finally {
      setSalvandoEdicao(false);
    }
  }

  async function acao(caminho: string, corpo?: unknown, metodo: 'post' | 'delete' = 'post') {
    try {
      if (metodo === 'delete') await api.delete(caminho);
      else await api.post(caminho, corpo);
      if (metodo === 'delete') navegar(`/autorizacoes/${id}?aba=acompanhamento`);
      else carregar();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Não deu para fazer isso.');
    }
  }

  if (carregando) {
    return (
      <div className="container py-4 d-flex align-items-center gap-2 text-secondary">
        <span className="spinner-border spinner-border-sm" /> Carregando…
      </div>
    );
  }

  const etapa = etapas.find((e) => String(e.id) === etapaId) ?? null;
  if (erro || !aut || !etapa) {
    return (
      <div className="container py-4">
        <div className="alert alert-light">{erro ?? 'Etapa não encontrada.'}</div>
        <Link to={`/autorizacoes/${id}?aba=acompanhamento`} className="btn btn-outline-theme">Voltar ao acompanhamento</Link>
      </div>
    );
  }

  const st = selo(etapa);
  const fl = fila(etapa);
  const numero = numeroDe(etapas, etapa.id);
  const outras = etapas.filter((e) => e.id !== etapa.id);
  const anteriores = etapa.depende_de
    .map((x) => etapas.find((e) => e.id === x))
    .filter(Boolean) as Etapa[];
  const seguintes = etapas.filter((e) => e.depende_de.includes(etapa.id));

  return (
    <CustomMolduraPagina
      /* ⛔ ONDE ESTOU, E DE QUE CASO. Dele, 11/09, testando em produção: *"a
         gente tem que saber onde a gente está; se eu estou dentro de uma etapa
         ou dentro de uma autorização, isso ainda não está bem claro"*.

         O esqueleto da página NÃO muda — decisão dele no mesmo dia: breadcrumb
         fica, selos ficam, nada de frase em português nem de emoji. O que muda
         é só a área do título: a autorização sobe para cima dele, em tamanho de
         leitura, e o NÚMERO da etapa entra no destaque em vez de ficar na linha
         cinza de baixo, onde ele se perdia entre estado e prazo. */
      /* ⛔ QUEM CARREGA O DESTAQUE É "ETAPA DA AUTORIZAÇÃO", não o nome dela.
         Dele, 12/09, olhando a primeira versão: *"a etapa da autorização é que
         deveria estar destacada"*. E faz sentido — o que a pessoa não sabe é
         ONDE ESTÁ; o nome da autorização ela reconhece, é o caso dela.

         ⛔ E O NOME NÃO É LINK. *"Eu não quero que o título tenha um hiperlink,
         quero que seja só o nome"*. Voltar já tem dois caminhos na tela (o
         "Voltar" acima e o breadcrumb abaixo); um terceiro, escondido num texto
         que parece rótulo, só engana quem toca sem querer. */
      sobrescrito={
        <>
          <strong className="casaa-moldura-sobrescrito-termo">Etapa da autorização</strong>
          {' · '}
          {aut.titulo}
        </>
      }
      titulo={`Etapa ${numero} ${etapa.titulo}`}
      /* ⛔ AS TRÊS LINHAS NO MESMO TAMANHO — só o NEGRITO separa. Escolha dele em
         12/09, depois de eu errar duas vezes: *"eu gostaria que os tamanhos
         fossem mais ou menos os mesmos; eu só queria que tivesse um destaque
         para o termo, e 'Etapa 1' também em bold. Só isso."*

         Então nesta tela o título não é a linha grande da página: ele tem o
         corpo das outras duas, e quem guia o olho é "Etapa da autorização" em
         cima e "Etapa 1" aqui. O `<h5>` continua sendo `<h5>` — o que muda é o
         tamanho desenhado, não a estrutura do documento, que é o que leitor de
         tela e sumário usam. */
      tituloExibido={
        <span className="casaa-moldura-titulo-plano">
          <strong>Etapa {numero}</strong> {etapa.titulo}
        </span>
      }
      descricao={`${st.rotulo}${fl ? ` · ${fl.rotulo}` : ''}${etapa.prazo ? ` · prazo ${formatarData(etapa.prazo)}` : ''}`}
      caminho={[
        { rotulo: 'Autorizações', para: '/autorizacoes' },
        { rotulo: aut.titulo, para: `/autorizacoes/${aut.id}?aba=acompanhamento` },
      ]}
      /* O degrau é só "Etapa 1": a frase inteira já está dois dedos acima, no
         título. Dele, 12/09 — *"a gente já tem esse mesmo texto na segunda
         linha da página"*. */
      rotuloNoCaminho={`Etapa ${numero}`}
      voltar={{ titulo: aut.titulo, caminho: `/autorizacoes/${aut.id}?aba=acompanhamento` }}
      ajuda="autorizacao-acompanhamento"
      acoes={
        /* OS DOIS SELOS, cada um com a sua dica: o primeiro é o ESTADO, que
           alguém escolhe; o segundo é a FILA, que ninguém escolhe. É a
           distinção que custou três tentativas de vocabulário, e é a que a
           pessoa precisa entender no primeiro dia. */
        <span className="d-inline-flex gap-2 align-items-center">
          <CustomDica lado="bottom" texto="Em que ponto a etapa está. Muda no campo Situação.">
            <span className={`badge ${st.cor} fs-6`}>{st.rotulo}</span>
          </CustomDica>
          {fl && (
            <CustomDica
              lado="bottom"
              texto={fl.rotulo === 'Bloqueada'
                ? 'Esta etapa espera outra terminar — não se escolhe, sai das dependências. Enquanto estiver assim, ninguém a assume.'
                : 'As etapas de que esta depende já terminaram: pode ser assumida. Não se escolhe — sai das dependências.'}
            >
              <span className={`badge ${fl.cor} fs-6`}>{fl.rotulo}</span>
            </CustomDica>
          )}
        </span>
      }
    >
      {/* AS ABAS FICAM NO TOPO DA PÁGINA — dele, 05/09: *"volta atividades para
          a parte superior, faz a aba. A aba principal se chama Etapa, e a
          segunda vai ser Atividades"*. Abaixo do formulário elas ficavam
          escondidas, e a pessoa não sabia onde registrar.

          ⚠️ `adminuiux-tabs` é o padrão do app — aba com o sublinhado do tema.
          **Pílula não é o nosso**, e ele já tinha corrigido isso em 04/09 no
          detalhe da autorização. `casaa-abas-rolam` porque as quatro não cabem
          em 390px: quebravam, e "Chat" caía sozinho numa segunda linha que já
          não parecia aba (10/09). */}
      <Tabs
        activeKey={abaEtapa}
        onSelect={(k) => k && setAbaEtapa(k as typeof abaEtapa)}
        className="adminuiux-tabs casaa-abas-rolam mb-3"
      >
        <Tab eventKey="etapa" title="Etapa">
      <div className="row gx-3 gx-lg-4">
        {/* ─── O ASSUNTO E A CONVERSA ───────────────────────────────────── */}
        {/* O CORPO — o formulário da etapa e o chat.
            A coluna encolheu de `col-lg-9` para `col-lg-7` em 05/09: a lateral
            passou a carregar o REGISTRO DE ATIVIDADES, que é texto e precisa de
            largura. Dele: *"talvez o melhor lugar seja o registro de atividade
            ali no painel lateral… só que aí ela teria que ser um pouquinho mais
            larga"*. */}
        <div className="col-12 col-lg-7 col-xl-8">
          <div className="card adminuiux-card mb-3 mb-lg-4">
            <div className="card-body">
              {editando ? (
                /* O FORMULÁRIO — rótulo flutuante em todo campo, que é o padrão
                   do projeto. Mesmo conteúdo do card, agora editável. */
                <div className="row g-3">
                  <div className="col-12">
                    <CampoComDica dica="A tarefa em uma frase, como se diria a quem vai fazer: “Negociar com a Hilza”, “Acompanhar a emissão da nota”. É o que aparece no card e no acompanhamento.">
                      <div className="form-floating">
                        <input id="etapa-e-titulo" className="form-control" placeholder=" "
                               value={eTitulo} onChange={(ev) => setETitulo(ev.target.value)}
                               maxLength={200} />
                        <label htmlFor="etapa-e-titulo">O que precisa ser feito</label>
                      </div>
                    </CampoComDica>
                  </div>
                  <div className="col-12">
                    <CampoComDica dica="O contexto que não cabe no título: com quem falar, o que já foi combinado, o que não pode ser esquecido. Quem pegar esta etapa amanhã lê isto primeiro.">
                      <div className="form-floating">
                        <textarea id="etapa-e-detalhe" className="form-control" placeholder=" "
                                  style={{ height: '7rem' }}
                                  value={eDetalhe} onChange={(ev) => setEDetalhe(ev.target.value)} />
                        <label htmlFor="etapa-e-detalhe">Explique a etapa</label>
                      </div>
                    </CampoComDica>
                  </div>
                  <div className="col-sm-6">
                    <CampoComDica dica="De quem é a vez, e quem aparece cobrado quando o prazo vence — mas não é exclusividade: qualquer um do círculo mexe na etapa. Ao criar, já vem preenchido com você.">
                      <div className="form-floating">
                        <select id="etapa-e-dono" className="form-select" value={eResponsavel}
                                onChange={(ev) => setEResponsavel(ev.target.value)}>
                          <option value="">— sem responsável ainda —</option>
                          {usuarios.map((u) => (
                            <option key={u.id} value={String(u.id)}>{u.nome}</option>
                          ))}
                        </select>
                        <label htmlFor="etapa-e-dono">Responsável</label>
                      </div>
                    </CampoComDica>
                  </div>
                  <div className="col-sm-6">
                    <CampoComDica dica="Quando isto precisa estar resolvido. Passou do dia, a etapa aparece com a marca de vencida em toda tela onde ela apareça. Adiar é possível — e sempre pede o motivo.">
                      <CustomCampoData id="etapa-e-prazo" label="Prazo (opcional)" valor={ePrazo}
                                       onChange={setEPrazo} />
                    </CampoComDica>
                  </div>
                  <div className="col-sm-6">
                    <CampoComDica dica={<>
                      Em que ponto a etapa está: <strong>Não iniciada</strong>, <strong>Em andamento</strong> (alguém assumiu),
                      {' '}<strong>Concluída</strong> ou <strong>Dispensada</strong> (deixou de ser necessária).
                      A tag ao lado — Liberada ou Bloqueada — não se escolhe: ela sai das etapas de que esta depende.
                    </>}>
                    <div className="form-floating">
                      <select id="etapa-e-situacao" className="form-select" value={eSituacao}
                              onChange={(ev) => setESituacao(ev.target.value as typeof eSituacao)}>
                        {/* "Não iniciada", e não "Liberada / bloqueada" — ele
                            não entendeu o rótulo, com razão: as duas são a mesma
                            `aberta`, e quem decide entre elas são as
                            DEPENDÊNCIAS, não a pessoa. O que se escolhe aqui é
                            voltar ao começo; se a etapa fica bloqueada ou
                            liberada depois disso, quem diz é a fila. */}
                        <option value="aberta">Não iniciada</option>
                        {/* "Em andamento" segue a MESMA trava do botão Assumir,
                            e olha o responsável ESCOLHIDO NO FORMULÁRIO, não o
                            que está gravado: quem se põe como responsável e
                            marca "em andamento" de uma vez faz as duas coisas
                            num salvamento só — o PUT do responsável vai antes. */}
                        {/* Sem trava de responsável desde 12/09: mudar a
                            situação é dar ciência de que se está mexendo, não
                            tomar posse. Bloqueada continua fora — quem manda
                            ali é a dependência. */}
                        <option value="em_andamento" disabled={!etapa.na_vez}>
                          Em andamento
                        </option>
                        <option value="feita">Concluída</option>
                        <option value="dispensada">Dispensada</option>
                      </select>
                      <label htmlFor="etapa-e-situacao">Situação</label>
                    </div>
                    </CampoComDica>
                    {/* Duas razões diferentes para "Em andamento" não estar
                        disponível, e cada uma diz a sua — a fila manda primeiro,
                        como no serviço. Dizer "mude o responsável" numa etapa
                        bloqueada mandaria a pessoa fazer o que não resolve. */}
                    {/* ⚠️ SÓ ENQUANTO NÃO COMEÇOU. Explicar "só quem responde
                        pela etapa a põe em andamento" numa etapa que JÁ está em
                        andamento é ruído — e ruído embaixo de campo é o que
                        ensina a pessoa a não ler o que está embaixo de campo. */}
                    {etapa.estado !== 'aberta' ? null : !etapa.na_vez ? (
                      <p className="form-text">
                        Esta etapa começa depois que a anterior terminar.
                      </p>
                    ) : null}
                  </div>
                </div>
              ) : (
                <>
              <div className="row gx-3 align-items-center mb-3">
                <div className="col">
                  <h4>{etapa.titulo}</h4>
                </div>
              </div>
              <div className="row gx-3 align-items-center mb-3">
                <div className="col-auto">
                  {etapa.responsavel_foto ? (
                    <figure className="avatar avatar-40 rounded-circle casaa-foto-no-avatar vm">
                      <img src={etapa.responsavel_foto} alt="" />
                    </figure>
                  ) : (
                    <span className="avatar avatar-40 rounded-circle bg-theme-1-subtle text-theme-1 vm">
                      <i className="bi bi-person"></i>
                    </span>
                  )}
                </div>
                <div className="col">
                  <p className="small text-secondary mb-0">Responsável</p>
                  <p className="mb-0">
                    {etapa.responsavel_nome ?? <span className="text-secondary fst-italic">sem dono</span>}
                    {etapa.criador_nome && (
                      <small className="text-secondary">
                        {' '}criada por {etapa.criador_nome}
                        {etapa.criado_em ? ` em ${formatarData(etapa.criado_em.slice(0, 10))}` : ''}
                      </small>
                    )}
                  </p>
                </div>
                <div className="col-auto text-end">
                  <p className="small text-secondary mb-0">Prazo</p>
                  <p className={`mb-0 ${etapa.vencida ? 'text-danger' : ''}`}>
                    {etapa.prazo ? formatarData(etapa.prazo) : '—'}
                    {etapa.adiamentos > 0 && (
                      <small className="text-secondary"> ↻ {etapa.adiamentos}×</small>
                    )}
                  </p>
                </div>
              </div>
              <p className="text-secondary">
                {etapa.detalhe?.trim() || 'Nada registrado ainda nesta etapa.'}
              </p>
              <p>
                <span className={`badge badge-lg ${st.cor} me-1 mt-1`}>
                  {st.rotulo}
                </span>
                <span className="badge badge-lg badge-light text-bg-theme-1 me-1 mt-1">
                  Etapa {numero}
                </span>
              </p>

              {adiando && etapa.estado !== 'feita' && etapa.estado !== 'dispensada' && (
                <div className="casaa-etapa-concluir mt-3">
                  <label className="form-label small text-secondary" htmlFor="etapa-motivo-adiar">
                    Por que está adiando? — é isto que se lê daqui a um mês
                  </label>
                  <div className="input-group">
                    <input
                      id="etapa-motivo-adiar"
                      className="form-control border"
                      placeholder="Liguei para a PC, não tinha ninguém…"
                      value={motivoAdiar}
                      onChange={(ev) => setMotivoAdiar(ev.target.value)}
                      maxLength={2000}
                      autoFocus
                    />
                    <button
                      type="button"
                      className="btn btn-theme"
                      disabled={!motivoAdiar.trim()}
                      onClick={async () => {
                        await acao(`/autorizacoes/etapas/${etapa.id}/adiamento`, {
                          dias: 7, motivo: motivoAdiar.trim(),
                        });
                        setMotivoAdiar('');
                        setAdiando(false);
                      }}
                    >
                      Adiar uma semana
                    </button>
                  </div>
                </div>
              )}

              {concluindo && etapa.estado !== 'feita' && etapa.estado !== 'dispensada' && (
                <div className="mt-3">
                  <CustomEtapaConclusao
                    etapaId={etapa.id}
                    usuarios={usuarios}
                    aoFechar={(gravou) => { setConcluindo(false); if (gravou) carregar(); }}
                  />
                </div>
              )}

              {/* ⚠️ O DESFECHO SÓ EXISTE QUANDO ACABOU. Com `!== 'aberta'`, a
                  etapa EM ANDAMENTO caía aqui e a tela escrevia "Dispensada"
                  numa etapa que ninguém dispensou — mentira, não desalinho. */}
              {!etapaAberta(etapa) && (
                <div className="casaa-etapa-desfecho">
                  <p className="small text-secondary mb-1">
                    {etapa.estado === 'feita' ? 'Concluída' : 'Dispensada'}
                    {etapa.concluinte_nome ? ` por ${etapa.concluinte_nome}` : ''}
                    {etapa.conclusao ? ` em ${formatarData(etapa.conclusao.slice(0, 10))}` : ''}
                  </p>
                  {etapa.observacao_conclusao && <p className="mb-0">{etapa.observacao_conclusao}</p>}
                </div>
              )}
                </>
              )}
            </div>

            {/* ⛔ DOIS EIXOS, DUAS FAIXAS — e eles estavam na mesma.
                Dele, 12/09: *"você tem Salvar, beleza. Concluir significa o quê?
                Encerrar a etapa? Você está na etapa, escreve o que precisa ser
                feito, e aí pergunta 'salvar' e 'concluir'? Como assim? É concluir
                o cadastro, é salvar a etapa? Bati o olho e fiquei em dúvida."*

                Ele tem razão: **Salvar é do CADASTRO** (grava o que você digitou
                nos campos acima) e **Concluir é do TRABALHO** (a tarefa acabou).
                Lado a lado, sem rótulo, "Concluir" se lê como "concluir o
                preenchimento" — a leitura óbvia de quem acabou de digitar.
                Separados e nomeados, a dúvida não nasce.

                E TODO botão daqui ganhou dica, não só o que ele apontou: *"eu
                ainda acho que você se recusa a colocar tooltip nas coisas"* —
                justo, a regra 8c da lei visual é de hoje de manhã e eu vinha
                aplicando só onde ele reclamava. */}
            {podeGerir && (
              <div className="card-footer">
                {editando && (
                  <div className="d-flex flex-wrap align-items-center gap-2 pb-3 mb-3 border-bottom">
                    <span className="small text-secondary me-1">O cadastro:</span>
                    <button type="button" className="btn btn-theme"
                            onClick={() => salvarEdicao(etapa)}
                            disabled={salvandoEdicao || !eTitulo.trim()}>
                      {salvandoEdicao
                        ? <span className="spinner-border spinner-border-sm me-1" />
                        : <i className="bi bi-check2 me-1"></i>}
                      Salvar
                    </button>
                    <CustomDica texto="Grava o que você mudou nos campos acima — o que precisa ser feito, a explicação, o responsável e o prazo. Não mexe no andamento da etapa." />
                  </div>
                )}

                <div className="d-flex flex-wrap align-items-center gap-2">
                  <span className="small text-secondary me-1">A etapa:</span>
                  {/* ⛔ NÃO VOLTE COM O BOTÃO "ASSUMIR". Ele saiu em 12/09,
                      depois de ele usar: *"eu concordo com a retirada do botão
                      assumir, não com o status de em andamento… basta ter a
                      alternativa de alterar o status"*. Pôr a etapa em
                      andamento é dar ciência de que se está mexendo, e isso é
                      MUDAR UM CAMPO — não um gesto com botão próprio, nem
                      tomar posse. Quem faz isso agora é o campo Situação. */}
                  {etapa.estado === 'aberta' || etapa.estado === 'em_andamento' ? (
                    <>
                      <button type="button" className="btn btn-outline-theme"
                              onClick={() => { setAdiando(false); setConcluindo((v) => !v); }}>
                        <i className="bi bi-check2-circle me-1"></i>Marcar como concluída
                      </button>
                      <CustomDica texto="O trabalho desta etapa acabou. Ela sai da fila, libera as que dependiam dela, e você conta o que foi feito. Não é salvar: é dizer que terminou." />
                      <button type="button" className="btn btn-outline-secondary"
                              onClick={() => { setConcluindo(false); setAdiando((v) => !v); }}>
                        <i className="bi bi-calendar-plus me-1"></i>Adiar uma semana
                      </button>
                      <CustomDica texto="Empurra o prazo em sete dias, com o motivo escrito. O adiamento fica no registro — não é o prazo sumindo, é ele mudando à vista de todos." />
                    </>
                  ) : (
                    <>
                      <button type="button" className="btn btn-outline-theme"
                              onClick={() => acao(`/autorizacoes/etapas/${etapa.id}/reabertura`)}>
                        <i className="bi bi-arrow-counterclockwise me-1"></i>Reabrir
                      </button>
                      <CustomDica texto="Volta a etapa para a fila: ela deixa de contar como terminada e as seguintes voltam a esperar por ela." />
                    </>
                  )}
                  {/* O `ms-auto` vai no GRUPO, não no botão: preso ao botão, ele
                      empurrava o "Excluir" para a direita e deixava a dica
                      órfã numa linha só dela. */}
                  <span className="d-inline-flex align-items-center gap-2 ms-auto">
                    <button type="button" className="btn btn-link text-danger"
                            onClick={() => acao(`/autorizacoes/etapas/${etapa.id}`, undefined, 'delete')}>
                      <i className="bi bi-trash3 me-1"></i>Excluir
                    </button>
                    <CustomDica lado="left" texto="Apaga a etapa do acompanhamento, com o que foi conversado nela. Não desfaz nada do que já aconteceu fora do sistema." />
                  </span>
                </div>
              </div>
            )}
          </div>

        </div>

        {/* ─── A COLUNA: ONDE ESTA ETAPA ESTÁ ─────────────────────────────
            As ATIVIDADES saíram daqui em 05/09, junto com a decisão de pôr o
            registro no topo. Se a aba registra e a lateral lê, a mesma coisa
            aparece duas vezes na mesma tela — e ninguém sabe qual olhar. A
            lateral volta a ser CONTEXTO: o que não cabe no corpo e some quando
            se sai da lista. */}
        <div className="col-12 col-lg-5 col-xl-4">
          {/* ⛔ A CONVERSA APARECE AQUI TAMBÉM, sem matar a aba Chat. Dele,
              11/09: *"eu não mataria a aba do chat, mas criaria um painelzinho
              simples de chat para as pessoas entrarem ali já verem o que tem"*.
              É o MESMO chat da autorização (uma sala só, decidido em 04/09) —
              a prévia lê o que `salasDoItem` já traz e leva para a aba. */}
          <div className="mb-3 mb-lg-4">
            <CustomMensagensPrevia
              ancora={{ tipo: 'autorizacoes', id: etapa.autorizacao_id }}
              aoAbrir={() => setAbaEtapa('chat')}
              etapaId={etapa.id}
              numerosDasEtapas={numerosPorEtapa}
            />
          </div>

          {/* De onde ela vem e para onde vai — a ordem, dita em palavras. */}
          {(anteriores.length > 0 || seguintes.length > 0) && (
            <div className="card adminuiux-card mb-3 mb-lg-4">
              <div className="card-header border-bottom"><h6 className="mb-0">A ordem</h6></div>
              <div className="card-body py-2">
                {anteriores.length > 0 && (
                  <p className="small text-secondary mb-2">
                    <i className="bi bi-arrow-up me-1"></i>
                    Só começa depois de: {anteriores.map((e) => e.titulo).join(' · ')}
                  </p>
                )}
                {seguintes.length > 0 && (
                  <p className="small text-secondary mb-0">
                    <i className="bi bi-arrow-down me-1"></i>
                    Destrava: {seguintes.map((e) => e.titulo).join(' · ')}
                  </p>
                )}
              </div>
            </div>
          )}

          {outras.length > 0 && (
            <div className="card adminuiux-card mb-3 mb-lg-4">
              <div className="card-header border-bottom"><h6 className="mb-0">As outras etapas</h6></div>
              <div className="card-body p-0">
                <div className="list-group list-group-flush bg-none">
                  {outras.map((e) => {
                    const s = selo(e);
                    return (
                      <Link
                        key={e.id}
                        to={`/autorizacoes/${id}/etapas/${e.id}`}
                        className="list-group-item list-group-item-action"
                      >
                        {/* O selo NÃO encolhe: um "F…" no lugar de "Feita" não
                            informa nada. Quem cede largura é o título. */}
                        <div className="d-flex align-items-start gap-2">
                          <span className="casaa-etapa-numero pequeno flex-shrink-0">
                            {numeroDe(etapas, e.id)}
                          </span>
                          <span className="flex-grow-1 min-width-0">{e.titulo}</span>
                        </div>
                        <div className="ps-4 mt-1">
                          <span className={`badge badge-light text-bg-theme-1 ${s.cor}`}>{s.rotulo}</span>
                        </div>
                      </Link>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
        </Tab>

        <Tab eventKey="atividades" title="Atividades">
          <div className="card adminuiux-card mb-3 mb-lg-4">
            <div className="card-body">
              <CustomAtividades
                key={`${etapa.id}-${etapa.estado}-${etapa.adiamentos}-${etapa.responsavel_id ?? 0}`}
                autorizacaoId={aut.id}
                etapaId={etapa.id}
                podeEscrever={podeGerir}
              />
            </div>
          </div>
        </Tab>

        <Tab eventKey="documentos" title="Documentos">
          <div className="card adminuiux-card mb-3 mb-lg-4">
            <div className="card-body">
              {/* O que sobe aqui entra POR ESTA ETAPA — a dona continua sendo a
                  autorização, e o documento aparece na coleção do caso. */}
              <CustomDocumentos
                autorizacaoId={aut.id}
                etapaId={etapa.id}
                podeEscrever={podeGerir}
              />
            </div>
          </div>
        </Tab>

        <Tab
          eventKey="chat"
          title={
            <span className="d-inline-flex align-items-center">
              Chat
              <CustomMensagensBadge ancora={{ tipo: 'autorizacoes', id: etapa.autorizacao_id }} />
            </span>
          }
        >
          <div className="card adminuiux-card mb-3 mb-lg-4">
            <div className="card-body">
              {/* ⛔ A ÂNCORA É A AUTORIZAÇÃO, NÃO A ETAPA (migration 095).
                  A conversa é do CASO: a etapa entra como `etapaId`, que marca
                  o que se escreve daqui e filtra o que se lê.

                  Dele, 05/09, duvidando do desenho que ele mesmo pediu: *"de
                  repente você vai começar a se perder — essa mensagem aqui a
                  gente mandou em qual etapa?"*. Cinco salas por autorização
                  fariam a pessoa escolher onde falar, e escolher onde falar é
                  trabalho que só existia por causa da estrutura. */}
              <CustomMensagensDoItem
                ancora={{ tipo: 'autorizacoes', id: etapa.autorizacao_id }}
                tituloSugerido={aut?.titulo ?? etapa.titulo}
                abrirDireto
                chatUnico
                etapaId={etapa.id}
                numerosDasEtapas={numerosPorEtapa}
              />
            </div>
          </div>
        </Tab>
      </Tabs>
    </CustomMolduraPagina>
  );
}
